
import React, { useEffect, useMemo } from 'react';
import { GenericScheduleProps, Schedule17TaxLossData, TaxLossItem, TaxLossTransferItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button'; 
import { safeParseFloat } from '../../utils/calculations'; 

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);

export const Schedule17TaxLosses: React.FC<GenericScheduleProps<Schedule17TaxLossData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handleItemChange = (listName: keyof Schedule17TaxLossData, index: number, field: keyof TaxLossItem | keyof TaxLossTransferItem, value: string) => {
    const list = (scheduleData[listName] as Array<TaxLossItem | TaxLossTransferItem>).slice();
    list[index] = { ...list[index], [field]: value };
    onChange(listName as keyof Schedule17TaxLossData, list);
  };

  const addItem = (listName: keyof Schedule17TaxLossData) => {
    const list = (scheduleData[listName] as Array<TaxLossItem | TaxLossTransferItem>).slice();
    let newItem: TaxLossItem | TaxLossTransferItem;
    if(listName === 'broughtForwardLosses' || listName === 'taxLossesReceivedBizRestructuring') {
        newItem = {id: Date.now().toString(), description: '', amount: ''};
    } else { 
        newItem = {id: Date.now().toString(), entityName: '', trn: '', amount: ''};
    }
    onChange(listName as keyof Schedule17TaxLossData, [...list, newItem]);
  };

  const removeItem = (listName: keyof Schedule17TaxLossData, id: string) => {
    const list = (scheduleData[listName] as Array<TaxLossItem | TaxLossTransferItem>).filter(item => item.id !== id);
    onChange(listName as keyof Schedule17TaxLossData, list);
  };

  const taxableIncomeBeforeLossUtilisation = useMemo(() => {
    return fullFormData && fullFormData.taxLiability ? safeParseFloat(fullFormData.taxLiability.taxableIncomeLossBeforeTaxLossUtilisation) : 0;
  }, [fullFormData]);


  useEffect(() => {
    let totalBFLoss = scheduleData.broughtForwardLosses.reduce((sum, item) => sum + safeParseFloat(item.amount), 0);
    totalBFLoss += scheduleData.taxLossesReceivedBizRestructuring.reduce((sum, item) => sum + safeParseFloat(item.amount), 0);
    
    const currentPeriodLossVal = safeParseFloat(fullFormData.taxLiability.taxableIncomeLossBeforeTaxLossUtilisation) < 0 
                                ? Math.abs(safeParseFloat(fullFormData.taxLiability.taxableIncomeLossBeforeTaxLossUtilisation))
                                : 0;
    
    if (scheduleData.taxLossIncurredCurrentPeriod !== currentPeriodLossVal.toFixed(2)) {
      onChange('taxLossIncurredCurrentPeriod', currentPeriodLossVal.toFixed(2));
      // Note: This will trigger another re-run of this useEffect, which is acceptable here.
    }
                                
    const limitedByOwnershipChange = safeParseFloat(scheduleData.taxLossesLimitedChangeOwnership);
    const otherAdjustIncrease = safeParseFloat(scheduleData.otherAdjustmentsIncrease);
    const otherAdjustDecrease = safeParseFloat(scheduleData.otherAdjustmentsDecrease);

    let availableLosses = totalBFLoss + currentPeriodLossVal - limitedByOwnershipChange + otherAdjustIncrease - otherAdjustDecrease;
    availableLosses = Math.max(0, availableLosses);

    let utilisedFromSchedule = 0;
    if (taxableIncomeBeforeLossUtilisation > 0) {
        const maxUtilisable = taxableIncomeBeforeLossUtilisation * 0.75;
        utilisedFromSchedule = Math.min(availableLosses, maxUtilisable);
    }
    
    const lossesAfterOwnUtilisation = availableLosses - utilisedFromSchedule;
    
    const totalTransferredToOthersGeneral = scheduleData.taxLossesTransferredToOthers.reduce((sum, item) => sum + safeParseFloat(item.amount), 0);
    const totalTransferredBizRestructuring = safeParseFloat(scheduleData.taxLossesTransferredBizRestructuring);

    const totalLossesTransferredOut = totalTransferredToOthersGeneral + totalTransferredBizRestructuring;
    // Ensure lossesAvailableForTransfer is not negative if totalLossesTransferredOut > lossesAfterOwnUtilisation
    const lossesAvailableForTransferCalc = Math.max(0, lossesAfterOwnUtilisation - totalLossesTransferredOut);
    
    const totalReceivedFromOthers = scheduleData.taxLossesReceivedFromOthers.reduce((sum, item) => sum + safeParseFloat(item.amount), 0);
    const finalCarriedForward = lossesAvailableForTransferCalc + totalReceivedFromOthers; 

    const newTotals: Partial<Schedule17TaxLossData> = {};
    if (scheduleData.taxLossesUtilisedInCurrentPeriodFromSchedule !== utilisedFromSchedule.toFixed(2)) {
        newTotals.taxLossesUtilisedInCurrentPeriodFromSchedule = utilisedFromSchedule.toFixed(2);
    }
    if (scheduleData.taxLossesCarriedForwardAvailableForTransfer !== lossesAvailableForTransferCalc.toFixed(2)) {
        newTotals.taxLossesCarriedForwardAvailableForTransfer = lossesAvailableForTransferCalc.toFixed(2);
    }
    if (scheduleData.taxLossesCarriedForwardFinal !== finalCarriedForward.toFixed(2)) {
        newTotals.taxLossesCarriedForwardFinal = finalCarriedForward.toFixed(2);
    }

    if (Object.keys(newTotals).length > 0) {
        for (const [key, value] of Object.entries(newTotals)) {
            onChange(key as keyof Schedule17TaxLossData, value);
        }
    }

  }, [
    scheduleData.broughtForwardLosses, 
    scheduleData.taxLossesReceivedBizRestructuring, 
    scheduleData.taxLossIncurredCurrentPeriod, // Keep this to react to its update
    fullFormData.taxLiability.taxableIncomeLossBeforeTaxLossUtilisation, // Added this for current period loss
    scheduleData.taxLossesLimitedChangeOwnership,
    scheduleData.otherAdjustmentsIncrease,
    scheduleData.otherAdjustmentsDecrease,
    scheduleData.taxLossesTransferredToOthers,
    scheduleData.taxLossesTransferredBizRestructuring,
    scheduleData.taxLossesReceivedFromOthers,
    taxableIncomeBeforeLossUtilisation, 
    onChange, 
    scheduleData.taxLossesUtilisedInCurrentPeriodFromSchedule,
    scheduleData.taxLossesCarriedForwardAvailableForTransfer,
    scheduleData.taxLossesCarriedForwardFinal
  ]);


  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('schedule17_title')}</h3>
      
      <SectionHeader title={t('s17_brought_forward_title')} />
      {scheduleData.broughtForwardLosses.map((item, index) => (
        <div key={item.id} className="p-2 border rounded-md space-y-2 relative">
          <Input label={t('s17_loss_description_label')} id={`bfLossDesc-${item.id}`} name={`bfLossDesc-${item.id}`} value={item.description} onChange={(e) => handleItemChange('broughtForwardLosses', index, 'description', e.target.value)} />
          <Input label={t('s17_loss_amount_label')} type="number" id={`bfLossAmount-${item.id}`} name={`bfLossAmount-${item.id}`} value={item.amount} onChange={(e) => handleItemChange('broughtForwardLosses', index, 'amount', e.target.value)} />
          <Button onClick={() => removeItem('broughtForwardLosses', item.id)} variant="danger_outline" size="sm" className="absolute top-1 right-1">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={() => addItem('broughtForwardLosses')} variant="secondary" size="sm">{t('add_item_button')}</Button>

      <Input label={t('s17_current_period_loss_label')} type="text" id="currentPeriodLoss" name="currentPeriodLoss" value={scheduleData.taxLossIncurredCurrentPeriod || '0.00'} readOnly className="bg-gray-100" />
      
      <SectionHeader title={t('s17_received_biz_restructuring_title')} />
      {scheduleData.taxLossesReceivedBizRestructuring.map((item, index) => (
         <div key={item.id} className="p-2 border rounded-md space-y-2 relative">
          <Input label={t('s17_loss_description_label')} id={`bizResLossDesc-${item.id}`} name={`bizResLossDesc-${item.id}`} value={item.description} onChange={(e) => handleItemChange('taxLossesReceivedBizRestructuring', index, 'description', e.target.value)} />
          <Input label={t('s17_loss_amount_label')} type="number" id={`bizResLossAmount-${item.id}`} name={`bizResLossAmount-${item.id}`} value={item.amount} onChange={(e) => handleItemChange('taxLossesReceivedBizRestructuring', index, 'amount', e.target.value)} />
          <Button onClick={() => removeItem('taxLossesReceivedBizRestructuring', item.id)} variant="danger_outline" size="sm" className="absolute top-1 right-1">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={() => addItem('taxLossesReceivedBizRestructuring')} variant="secondary" size="sm">{t('add_item_button')}</Button>
      
      <Input label={t('s17_transferred_biz_restructuring_title')} type="number" id="transferredBizRestructuring" name="transferredBizRestructuring" value={scheduleData.taxLossesTransferredBizRestructuring || ''} onChange={e => onChange('taxLossesTransferredBizRestructuring', e.target.value)} error={errors?.taxLossesTransferredBizRestructuring as string}/>


      <Input label={t('s17_limited_change_ownership_label')} type="number" id="limitedChangeOwnership" name="limitedChangeOwnership" value={scheduleData.taxLossesLimitedChangeOwnership} onChange={e => onChange('taxLossesLimitedChangeOwnership', e.target.value)} />
      
      <SectionHeader title={t('s17_other_adjustments_title')} />
      <Input label={t('s17_adjust_increase_label')} type="number" id="otherAdjustIncrease" name="otherAdjustIncrease" value={scheduleData.otherAdjustmentsIncrease} onChange={e => onChange('otherAdjustmentsIncrease', e.target.value)} />
      <Input label={t('s17_adjust_decrease_label')} type="number" id="otherAdjustDecrease" name="otherAdjustDecrease" value={scheduleData.otherAdjustmentsDecrease} onChange={e => onChange('otherAdjustmentsDecrease', e.target.value)} />
      <Input label={t('s17_adjust_desc_label')} type="text" id="otherAdjustDesc" name="otherAdjustDesc" value={scheduleData.otherAdjustmentsDesc} onChange={e => onChange('otherAdjustmentsDesc', e.target.value)} />

      <Input label={t('s10_2_losses_utilised_label')} id="taxLossesUtilisedInCurrentPeriodFromScheduleInput" name="taxLossesUtilisedInCurrentPeriodFromScheduleInput" type="text" value={scheduleData.taxLossesUtilisedInCurrentPeriodFromSchedule || '0.00'} readOnly className="bg-gray-100 font-semibold" />
      <Input label={t('s17_available_for_transfer_label')} id="taxLossesCarriedForwardAvailableForTransferInput" name="taxLossesCarriedForwardAvailableForTransferInput" type="text" value={scheduleData.taxLossesCarriedForwardAvailableForTransfer || '0.00'} readOnly className="bg-gray-100" />

      <SectionHeader title={t('s17_transferred_to_others_title')} />
        {scheduleData.taxLossesTransferredToOthers.map((item, index) => (
            <div key={item.id} className="p-2 border rounded-md space-y-2 relative">
                <Input label={t('s17_transfer_entity_name_label')} id={`transToEntityName-${item.id}`} name={`transToEntityName-${item.id}`} value={item.entityName} onChange={(e) => handleItemChange('taxLossesTransferredToOthers', index, 'entityName', e.target.value)} />
                <Input label={t('s17_transfer_trn_label')} id={`transToTrn-${item.id}`} name={`transToTrn-${item.id}`} value={item.trn} onChange={(e) => handleItemChange('taxLossesTransferredToOthers', index, 'trn', e.target.value)} />
                <Input label={t('s17_transfer_amount_label')} type="number" id={`transToAmount-${item.id}`} name={`transToAmount-${item.id}`} value={item.amount} onChange={(e) => handleItemChange('taxLossesTransferredToOthers', index, 'amount', e.target.value)} />
                <Button onClick={() => removeItem('taxLossesTransferredToOthers', item.id)} variant="danger_outline" size="sm" className="absolute top-1 right-1">{t('remove_item_button')}</Button>
            </div>
        ))}
      <Button onClick={() => addItem('taxLossesTransferredToOthers')} variant="secondary" size="sm">{t('add_item_button')}</Button>
      
      <SectionHeader title={t('s17_received_from_others_title')} />
       {scheduleData.taxLossesReceivedFromOthers.map((item, index) => (
            <div key={item.id} className="p-2 border rounded-md space-y-2 relative">
                <Input label={t('s17_transfer_entity_name_label')} id={`recFromEntityName-${item.id}`} name={`recFromEntityName-${item.id}`} value={item.entityName} onChange={(e) => handleItemChange('taxLossesReceivedFromOthers', index, 'entityName', e.target.value)} />
                <Input label={t('s17_transfer_trn_label')} id={`recFromTrn-${item.id}`} name={`recFromTrn-${item.id}`} value={item.trn} onChange={(e) => handleItemChange('taxLossesReceivedFromOthers', index, 'trn', e.target.value)} />
                <Input label={t('s17_transfer_amount_label')} type="number" id={`recFromAmount-${item.id}`} name={`recFromAmount-${item.id}`} value={item.amount} onChange={(e) => handleItemChange('taxLossesReceivedFromOthers', index, 'amount', e.target.value)} />
                <Button onClick={() => removeItem('taxLossesReceivedFromOthers', item.id)} variant="danger_outline" size="sm" className="absolute top-1 right-1">{t('remove_item_button')}</Button>
            </div>
        ))}
      <Button onClick={() => addItem('taxLossesReceivedFromOthers')} variant="secondary" size="sm">{t('add_item_button')}</Button>


      <Input label={t('s17_final_carried_forward_label')} type="text" id="finalCarriedForwardLoss" name="finalCarriedForwardLoss" value={scheduleData.taxLossesCarriedForwardFinal || '0.00'} readOnly className="bg-gray-100 font-semibold" />
    </div>
  );
};
